import type { CoAdminRegion, RoutePublic, UserPublic } from '@xingroute/schema'

export type RoutePermissionTier = 'admin' | 'co-admin' | 'creator' | 'editor'

export function resolveRoutePermissionTier(
  user: Pick<UserPublic, 'id' | 'role' | 'co_admin_region'>,
  route: Pick<RoutePublic, 'created_by' | 'region'>,
): RoutePermissionTier {
  if (user.role === 'admin') return 'admin'
  if (user.co_admin_region && regionMatches(user.co_admin_region, route.region)) return 'co-admin'
  if (user.id === route.created_by) return 'creator'
  return 'editor'
}

export function canDirectEditRoute(
  user: Pick<UserPublic, 'id' | 'role' | 'co_admin_region'>,
  route: Pick<RoutePublic, 'created_by' | 'region'>,
): boolean {
  const tier = resolveRoutePermissionTier(user, route)
  return tier === 'admin' || tier === 'co-admin' || tier === 'creator'
}

export function canModerateRouteProposals(
  user: Pick<UserPublic, 'id' | 'role' | 'co_admin_region'>,
  route: Pick<RoutePublic, 'created_by' | 'region'>,
): boolean {
  return canDirectEditRoute(user, route)
}

export function canApprovePublishRoute(
  user: Pick<UserPublic, 'id' | 'role' | 'co_admin_region'>,
  route: Pick<RoutePublic, 'created_by' | 'region'>,
): boolean {
  if (user.id === route.created_by) return false
  const tier = resolveRoutePermissionTier(user, route)
  return tier === 'admin' || tier === 'co-admin'
}

export function canShowPublishDirectRoute(
  user: Pick<UserPublic, 'id' | 'role' | 'co_admin_region'>,
  route: Pick<RoutePublic, 'created_by' | 'region' | 'status'>,
): boolean {
  if (route.status === 'pending_review') {
    return false
  }
  if (route.status === 'draft') return false
  return false
}

function regionMatches(adminRegion: CoAdminRegion, routeRegion?: RoutePublic['region']): boolean {
  if (!routeRegion) return false
  const norm = (value?: string): string | undefined => {
    if (typeof value !== 'string') return undefined
    const trimmed = value.trim().toLowerCase()
    return trimmed.length > 0 ? trimmed : undefined
  }
  const admin = {
    province: norm(adminRegion.province),
    city: norm(adminRegion.city),
    district: norm(adminRegion.district),
  }
  const route = {
    province: norm(routeRegion.province),
    city: norm(routeRegion.city),
    district: norm(routeRegion.district),
  }
  if (admin.district) {
    return (
      route.province === admin.province &&
      route.city === admin.city &&
      route.district === admin.district
    )
  }
  if (admin.city) {
    return route.province === admin.province && route.city === admin.city
  }
  if (admin.province) {
    return route.province === admin.province
  }
  return false
}
